import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Warning,
  ArrowRight,
  FileXls,
  ClipboardText,
} from '@phosphor-icons/react';
import { Skeleton } from '../../components/ui/skeleton';
import MesVisasCard from '../project/visas/MesVisasCard';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import MonEquipe from '../entreprise/MonEquipe';
import MesDocumentsEntreprise from '../entreprise/MesDocumentsEntreprise';
import DPGFInflightModificationsList from '../project/DPGFInflightModificationsList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardEntreprise({ user }) {
  const [chantiers, setChantiers] = useState([]);
  const [reserves, setReserves] = useState([]);
  // A6 (P2#8) — appels d'offres à chiffrer, en tête du tableau de bord
  const [appelsOffres, setAppelsOffres] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  // Sprint D — Téléchargement DPGF chiffré
  const downloadDpgfChiffre = async (chantierId, chantierName) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/entreprise/chantiers/${chantierId}/dpgf/export-excel`,
        { withCredentials: true, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      link.download = m ? m[1] : `DPGF_chiffre_${(chantierName || 'chantier').replace(/\s/g, '_')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('DPGF chiffré téléchargé');
    } catch (err) {
      const msg = err?.response?.data?.detail || (err?.response?.status === 404
        ? 'Aucun lot attribué à votre entreprise sur ce chantier'
        : 'Erreur téléchargement');
      toast.error(msg);
    }
  };

  const fetchData = async () => {
    try {
      const { data: chantiersData } = await axios.get(`${API_URL}/api/chantiers`, { withCredentials: true });
      setChantiers(chantiersData);
      const { data: ao } = await axios.get(`${API_URL}/api/entreprise/consultations`, { withCredentials: true })
        .catch(() => ({ data: [] }));
      setAppelsOffres(ao || []);

      // RP03 — Les compteurs « Situations en attente » (situations v1, jamais
      // alimentées) et « Devis soumis », ainsi que le bloc legacy « Mes tâches
      // issues des CRC » (db.taches, remplacé par db.elements) ont été retirés :
      // ils affichaient en permanence 0 ou des données obsolètes.
      const allReserves = [];
      for (const c of chantiersData.slice(0, 5)) {
        const resR = await axios
          .get(`${API_URL}/api/chantiers/${c.id}/reserves`, { withCredentials: true })
          .catch(() => ({ data: [] }));
        allReserves.push(...(resR.data || []).map(r => ({ ...r, project_name: c.name, project_id: c.id })));
      }
      setReserves(allReserves);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="space-y-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>;

  const openReserves = reserves.filter(r => r.status !== 'levee');
  const aChiffrer = appelsOffres
    .filter((c) => c.reponse_statut !== 'SOUMISE' && !c.hors_delai
      && ['EN_PREPARATION', 'EN_COURS', undefined, null].includes(c.consultation_statut))
    .sort((a, b) => String(a.date_limite || '9999').localeCompare(String(b.date_limite || '9999')));
  const retenus = appelsOffres.filter((c) => c.resultat === 'retenu');
  const dateFr = (d) => (d ? new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString('fr-FR') : '');

  return (
    <div className="space-y-6" data-testid="dashboard-entreprise">
      <div>
        <h1 className="text-2xl font-bold text-[#2C2C2C]" style={{ fontFamily: "'DM Serif Display', serif" }}>
          {user.name}
        </h1>
        <p className="text-sm text-[#6B6B6B]">Tableau de bord entreprise</p>
      </div>

      {/* A6 (P2#8) — « 1 appel d'offres à chiffrer avant le … → Chiffrer » */}
      {aChiffrer.length > 0 && (
        <div className="border-2 border-[#C75B39] bg-[#FFF8F3] px-4 py-3 flex flex-wrap items-center gap-3" data-testid="appel-offres-banner">
          <ClipboardText size={22} className="text-[#C75B39] flex-shrink-0" />
          <p className="flex-1 min-w-[180px] text-sm text-[#2C2C2C]">
            <strong>{aChiffrer.length} appel{aChiffrer.length > 1 ? 's' : ''} d'offres à chiffrer</strong>
            {aChiffrer[0].date_limite && <> avant le <strong>{dateFr(aChiffrer[0].date_limite)}</strong></>}
            <span className="text-[#6B6B6B]"> — {aChiffrer[0].chantier_nom}{aChiffrer.length > 1 ? ' et autres' : ''}</span>
          </p>
          <Link to={aChiffrer.length === 1 ? `/consultations?invitation=${aChiffrer[0].invitation_id}` : '/consultations'}
            className="inline-flex items-center gap-1 bg-[#C75B39] hover:bg-[#D97B5C] text-white text-sm font-semibold px-4 py-2"
            data-testid="appel-offres-chiffrer">
            Chiffrer <ArrowRight size={14} />
          </Link>
        </div>
      )}
      {/* A6 (P2#9) — résultat des appels d'offres */}
      {retenus.length > 0 && (
        <div className="border border-[#2D6A4F] bg-[#E8F5E9] px-4 py-2 text-sm text-[#2D6A4F]" data-testid="appel-offres-retenu">
          Retenu sur {retenus.map((c) => c.chantier_nom).join(', ')} : le chantier figure dans vos marchés actifs.
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="border border-[#E0E0DB] bg-white p-4">
          <p className="text-2xl font-bold text-[#1E3A5F]">{chantiers.length}</p>
          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Marchés actifs</p>
        </div>
        <div className="border border-[#E0E0DB] bg-white p-4">
          <p className="text-2xl font-bold text-[#C75B39]">{openReserves.length}</p>
          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Réserves ouvertes</p>
        </div>
      </div>

      {/* RP52-54 — visas à viser / déposés */}
      <MesVisasCard />

      {/* Mon équipe — A6 : seulement si le compte est rattaché à une entreprise */}
      {(user.parent_entreprise_id || user.entreprise_id || user.linked_entreprise_id) && (
      <div className="border border-[#E0E0DB] bg-[#FAFAF8] p-4 sm:p-5">
        <MonEquipe user={user} />
      </div>
      )}

      {/* Mes documents administratifs */}
      <div className="border border-[#E0E0DB] bg-[#FAFAF8] p-4 sm:p-5 overflow-hidden">
        <MesDocumentsEntreprise />
      </div>

      {/* Réserves */}
      <div className="border border-[#E0E0DB] bg-white">
        <div className="px-4 py-3 border-b border-[#E0E0DB]">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B]">Réserves à traiter</h2>
        </div>
        <div className="divide-y divide-[#E0E0DB]">
          {openReserves.length === 0 && <p className="text-sm text-[#9A9A9A] text-center py-6">Aucune réserve ouverte</p>}
          {openReserves.map(r => (
            <div key={r.id} className="px-4 py-3">
              <div className="flex items-start gap-2">
                <Warning size={16} className="text-[#C75B39] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#2C2C2C]">{r.lot}: {r.description}</p>
                  <p className="text-xs text-[#6B6B6B]">{r.project_name}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sprint D — Modifications DPGF in-flight (multi-chantiers) */}
      <DPGFInflightModificationsList chantiers={chantiers} />

      {/* Marchés actifs */}
      <div className="border border-[#E0E0DB] bg-white">
        <div className="px-4 py-3 border-b border-[#E0E0DB]">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B]">Marchés actifs</h2>
        </div>
        <div className="divide-y divide-[#E0E0DB]">
          {chantiers.map(c => (
            <div key={c.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-2 hover:bg-[#FAFAF9]" data-testid={`entreprise-chantier-row-${c.id}`}>
              <Link to={`/chantiers/${c.id}`} className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#2C2C2C]">{c.name}</p>
                <p className="text-xs text-[#6B6B6B]">{c.address}</p>
              </Link>
              <Button
                size="sm"
                variant="outline"
                className="rounded-none gap-1 text-xs h-7 mr-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
                onClick={() => downloadDpgfChiffre(c.id, c.name)}
                data-testid={`download-dpgf-chiffre-${c.id}`}
              >
                <FileXls size={12} /> Ma DPGF chiffrée
              </Button>
              <ArrowRight size={14} className="text-[#9A9A9A]" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
